import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    type: String,      // "photo", "project", "company", or "post"
    id: Number,
    favorited: Boolean,
    removeOnUnfavorite: Boolean
  }

  connect() {
    console.log('Favorites controller connected', {
      type: this.typeValue,
      id: this.idValue,
      favorited: this.favoritedValue
    })
    // Don't call updateUI on connect - the server has already rendered the correct state
    // Only call it after toggle() to update the UI
  }

  showAuthModal(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // Open the authentication modal
    if (typeof window.openAuthModal === 'function') {
      window.openAuthModal()
    }
  }

  toggle(event) {
    console.log('Toggle clicked', event)
    event.preventDefault()
    event.stopPropagation()

    // Add pulse animation to button
    const button = event.currentTarget
    button.classList.add('favoriting')
    setTimeout(() => button.classList.remove('favoriting'), 300)

    // Optimistically update UI
    const wasFavorited = this.favoritedValue
    this.favoritedValue = !wasFavorited
    this.updateUI()

    // Send AJAX request
    const url = wasFavorited 
      ? `/favorites/${this.typeValue}/${this.idValue}` 
      : `/favorites/${this.typeValue}/${this.idValue}`
    const method = wasFavorited ? 'DELETE' : 'POST'

    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'guest') {
        // Not logged in — revert UI and fire event to show email capture bar
        this.favoritedValue = wasFavorited
        this.updateUI()
        window.dispatchEvent(new CustomEvent('guestAction'))
      } else if (data.status === 'error') {
        // Revert on error
        this.favoritedValue = wasFavorited
        this.updateUI()
      } else if (data.status === 'unfavorited' && this.removeOnUnfavoriteValue) {
        this.removeFromSavedArea()
      }
    })
    .catch(error => {
      // Revert on error
      this.favoritedValue = wasFavorited
      this.updateUI()
      console.error('Favorite toggle error:', error)
    })
  }

  removeFromSavedArea() {
    const savedItem = this.element.closest('[data-saved-item]')
    if (savedItem) savedItem.remove()

    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: 'replace' })
    } else {
      window.location.reload()
    }
  }

  updateUI() {
    const button = this.element.matches('button') ? this.element : this.element.querySelector('button')
    if (!button) {
      console.error('Button not found in favorites controller')
      return
    }
    
    const svg = button.querySelector('svg')
    if (!svg) {
      console.error('SVG not found in button')
      return
    }
    
    if (this.favoritedValue) {
      const labelText = button.dataset.favoritesSavedLabel || `Saved ${this.typeName()}`
      const titleText = button.dataset.favoritesSavedTitle || labelText

      // Filled heart
      svg.innerHTML = `
        <path d="M12 21s-9-4.78-9-12c0-2.485 2.1-4.5 4.688-4.5 1.935 0 3.597 1.126 4.312 2.733C12.715 5.626 14.377 4.5 16.313 4.5 18.9 4.5 21 6.515 21 9c0 7.22-9 12-9 12z"/>
      `
      svg.setAttribute('fill', 'currentColor')
      svg.removeAttribute('stroke')
      svg.removeAttribute('stroke-width')
      this.updateIconColor(svg, button.dataset.favoritesSavedIconClass || 'text-primary-600')
      this.updateButtonText(button, labelText)
      button.title = titleText
      button.setAttribute('aria-label', titleText)
    } else {
      const labelText = button.dataset.favoritesUnsavedLabel || `Save ${this.typeName()}`
      const titleText = button.dataset.favoritesUnsavedTitle || labelText

      // Empty heart
      svg.innerHTML = `
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/>
      `
      svg.removeAttribute('fill')
      svg.setAttribute('stroke', 'currentColor')
      svg.setAttribute('stroke-width', '2')
      this.updateIconColor(svg, button.dataset.favoritesUnsavedIconClass || 'text-gray-700')
      this.updateButtonText(button, labelText)
      button.title = titleText
      button.setAttribute('aria-label', titleText)
    }
  }

  typeName() {
    return this.typeValue.charAt(0).toUpperCase() + this.typeValue.slice(1)
  }

  updateButtonText(button, text) {
    const label = button.querySelector('[data-favorites-label]')
    if (label) label.textContent = text
  }

  updateIconColor(svg, className) {
    svg.classList.remove('text-primary-600', 'text-white', 'text-gray-400', 'text-gray-700')
    svg.classList.add(className)
  }
}
